import React from 'react'
import { UseFormReturn } from 'react-hook-form'
import { CircularProgress } from '@mui/material'
import { H2, Line, Button, Flex } from '../../lib/components'
import { JobScheduleFormData, JobScheduleItem } from '../../services/job-schedule-service'
import './news-schedule-form.scss'

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/

// Cron day numbers: 0 is Sunday. Shown Monday first.
const DAY_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
]
const SHORT_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// IANA names the server accepts. Labels are shown in the dropdown.
const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'India (Asia/Kolkata)' },
  { value: 'Asia/Dubai', label: 'Dubai (Asia/Dubai)' },
  { value: 'Europe/London', label: 'UK (Europe/London)' },
  { value: 'America/New_York', label: 'USA Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'USA Central (America/Chicago)' },
  { value: 'America/Los_Angeles', label: 'USA Pacific (America/Los_Angeles)' },
]

const formatDateTime = (value: string | null) => {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

const formatDays = (days: number[] | null) => {
  if (!days || days.length === 0) return []
  return [...days].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b))
}

const timezoneLabel = (value: string) =>
  TIMEZONE_OPTIONS.find((zone) => zone.value === value)?.label ?? value

const STATUS_LABEL: Record<string, string> = {
  RUNNING: 'Running',
  SUCCEEDED: 'Succeeded',
  FAILED: 'Failed',
}

type NewsSchedulePresenterProps = {
  form: UseFormReturn<JobScheduleFormData>
  schedule: JobScheduleItem | null
  jobKey: string
  isSubmitting: boolean
  isLoading: boolean
  isStarting: boolean
  onSubmit: (data: JobScheduleFormData) => Promise<void>
  onToggleEnabled: (enabled: boolean) => Promise<void>
  onEditRecord: () => void
  onRunNow: () => Promise<void>
}

function NewsSchedulePresenter({
  form,
  schedule,
  isSubmitting,
  isLoading,
  isStarting,
  onSubmit,
  onToggleEnabled,
  onEditRecord,
  onRunNow,
}: NewsSchedulePresenterProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = form

  const frequency = watch('frequency')
  const selectedDays = watch('daysOfWeek') ?? []
  const isWeekly = frequency === 'WEEKLY'
  const savedDays = schedule ? formatDays(schedule.daysOfWeek) : []
  const statusKey = schedule?.lastStatus?.toLowerCase() ?? 'none'

  const toggleDay = (day: number) => {
    const next = selectedDays.includes(day)
      ? selectedDays.filter((value) => value !== day)
      : [...selectedDays, day]
    setValue('daysOfWeek', next, { shouldDirty: true })
    if (next.length > 0) clearErrors('daysOfWeek')
  }

  return (
    <>
      {isLoading && (
        <div className='loading-overlay'>
          <CircularProgress size={40} color='primary' className='loading-overlay__spinner' />
        </div>
      )}

      <Flex variant='column' classes={['news-schedule-page']}>
        <H2 classes={['mb-24']} text='News Fetch Schedule' />

        <div className='news-schedule-layout'>
          <form className='news-schedule-form' onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className='news-schedule-form__field'>
              <label className='news-schedule-form__label' htmlFor='name'>
                Scheduler name <span className='news-schedule-form__required'>*</span>
              </label>
              <input
                id='name'
                className={`news-schedule-form__input ${
                  errors.name ? 'news-schedule-form__input--error' : ''
                }`}
                placeholder='News fetch'
                maxLength={100}
                {...register('name', {
                  required: 'Scheduler name is required',
                  validate: (value) => value.trim().length > 0 || 'Scheduler name is required',
                  maxLength: { value: 100, message: 'Name must be at most 100 characters' },
                })}
              />
              {errors.name && (
                <span className='news-schedule-form__error'>{errors.name.message}</span>
              )}
            </div>

            <div className='news-schedule-form__field'>
              <label className='news-schedule-form__label' htmlFor='frequency'>
                Frequency <span className='news-schedule-form__required'>*</span>
              </label>
              <select
                id='frequency'
                className='news-schedule-form__input'
                {...register('frequency', { required: 'Frequency is required' })}
              >
                <option value='DAILY'>Daily</option>
                <option value='WEEKLY'>Weekly</option>
              </select>
              {errors.frequency && (
                <span className='news-schedule-form__error'>{errors.frequency.message}</span>
              )}
            </div>

            {isWeekly && (
              <div className='news-schedule-form__field'>
                <span className='news-schedule-form__label'>
                  Days of week <span className='news-schedule-form__required'>*</span>
                </span>
                <div className='news-schedule-form__days'>
                  {DAY_OPTIONS.map((day) => (
                    <label key={day.value} className='news-schedule-form__day'>
                      <input
                        type='checkbox'
                        checked={selectedDays.includes(day.value)}
                        onChange={() => toggleDay(day.value)}
                      />
                      {day.label}
                    </label>
                  ))}
                </div>
                {errors.daysOfWeek && (
                  <span className='news-schedule-form__error'>{errors.daysOfWeek.message}</span>
                )}
              </div>
            )}

            <div className='news-schedule-form__field'>
              <label className='news-schedule-form__label' htmlFor='timeOfDay'>
                Time of day <span className='news-schedule-form__required'>*</span>
              </label>
              <input
                id='timeOfDay'
                type='time'
                className={`news-schedule-form__input ${
                  errors.timeOfDay ? 'news-schedule-form__input--error' : ''
                }`}
                {...register('timeOfDay', {
                  required: 'Time of day is required',
                  pattern: {
                    value: TIME_REGEX,
                    message: 'Enter time as HH:MM (24-hour)',
                  },
                })}
              />
              {errors.timeOfDay && (
                <span className='news-schedule-form__error'>{errors.timeOfDay.message}</span>
              )}
            </div>

            <div className='news-schedule-form__field'>
              <label className='news-schedule-form__label' htmlFor='timezone'>
                Timezone <span className='news-schedule-form__required'>*</span>
              </label>
              <select
                id='timezone'
                className={`news-schedule-form__input ${
                  errors.timezone ? 'news-schedule-form__input--error' : ''
                }`}
                {...register('timezone', { required: 'Timezone is required' })}
              >
                {TIMEZONE_OPTIONS.map((zone) => (
                  <option key={zone.value} value={zone.value}>
                    {zone.label}
                  </option>
                ))}
              </select>
              {errors.timezone && (
                <span className='news-schedule-form__error'>{errors.timezone.message}</span>
              )}
            </div>

            <div className='news-schedule-form__actions'>
              <Button
                buttonType='submit'
                variant='primary-btn'
                handleClick={() => {}}
                text={isSubmitting ? 'Saving...' : 'Save Schedule'}
                isDisabled={isSubmitting}
              />
            </div>
          </form>

          {schedule && (
            <section className='news-schedule-summary' data-testid='news-schedule-table-container'>
              <div className='news-schedule-summary__header'>
                <div>
                  <p className='news-schedule-summary__eyebrow'>Saved schedule</p>
                  <h3 className='news-schedule-summary__name'>{schedule.name}</h3>
                </div>
                <div className='news-schedule-summary__header-meta'>
                  <span
                    className={`news-schedule-summary__status news-schedule-summary__status--${statusKey}`}
                  >
                    {schedule.lastStatus ? STATUS_LABEL[schedule.lastStatus] : 'Never run'}
                  </span>
                  <label className='news-schedule-summary__toggle'>
                    <span>{schedule.enabled ? 'On' : 'Off'}</span>
                    <input
                      type='checkbox'
                      checked={schedule.enabled}
                      disabled={isSubmitting}
                      aria-label='Enabled'
                      onChange={(e) => onToggleEnabled(e.target.checked)}
                    />
                    <span className='news-schedule-summary__switch' aria-hidden='true' />
                  </label>
                </div>
              </div>

              <dl className='news-schedule-summary__grid'>
                <div>
                  <dt>Frequency</dt>
                  <dd>{schedule.frequency === 'WEEKLY' ? 'Weekly' : 'Daily'}</dd>
                </div>
                <div>
                  <dt>Time</dt>
                  <dd>{schedule.timeOfDay}</dd>
                </div>
                <div>
                  <dt>Timezone</dt>
                  <dd>{timezoneLabel(schedule.timezone)}</dd>
                </div>
                <div>
                  <dt>Days</dt>
                  <dd>
                    {schedule.frequency === 'WEEKLY' && savedDays.length > 0 ? (
                      <span className='news-schedule-summary__days'>
                        {savedDays.map((day) => (
                          <span key={day} className='news-schedule-summary__day'>
                            {SHORT_DAY_NAMES[day]}
                          </span>
                        ))}
                      </span>
                    ) : (
                      'Every day'
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Next run</dt>
                  <dd className={schedule.enabled ? '' : 'news-schedule-summary__muted'}>
                    {schedule.enabled ? formatDateTime(schedule.nextRunAt) : 'Paused'}
                  </dd>
                </div>
                <div>
                  <dt>Last run</dt>
                  <dd className={schedule.lastRunAt ? '' : 'news-schedule-summary__muted'}>
                    {formatDateTime(schedule.lastRunAt)}
                  </dd>
                </div>
              </dl>

              <div className='news-schedule-summary__actions'>
                <Button
                  text={schedule.lastStatus === 'RUNNING' ? 'Running...' : 'Run now'}
                  variant='secondary-btn'
                  handleClick={onRunNow}
                  isDisabled={isStarting || schedule.lastStatus === 'RUNNING'}
                  classes={['news-schedule-summary__action-btn']}
                  dataTestId='news-schedule-run-now'
                />
              </div>
            </section>
          )}
        </div>
      </Flex>
    </>
  )
}

export { NewsSchedulePresenter }
